import { baseApi } from '../baseApi';

/** One month's two money totals. */
export interface MonthPoint {
  key: string;
  label: string;
  sales: number;
  purchases: number;
}

/** A named share of some whole — an order status, a category, a product. */
export interface NamedValue {
  key: string;
  label: string;
  value: number;
}

export interface DashboardStats {
  productCount: number;
  lowStockCount: number;
  outOfStockCount: number;
  /**
   * Non-overlapping buckets for the ring. `lowStockCount` above cannot be
   * reused: by its rule a zero-stock product with a reorder level is BOTH low
   * and out, so the parts would sum past the catalogue.
   */
  stockHealth: { healthy: number; low: number; out: number };
  orderCount: number;
  saleCount: number;
  /** Stock on hand valued at selling price. */
  inventoryValue: number;
  salesValue: number;
  purchaseValue: number;
  /** Whatever the catalogue is priced in; the charts label money with it. */
  currency?: string;
  monthly: MonthPoint[];
  orderStatus: NamedValue[];
  topProducts: NamedValue[];
  categoryValue: NamedValue[];
  /** Products per category, largest first — the WHOLE list, so the column
   *  chart can fold its own tail into an "Other" bar honestly. */
  categoryCount: NamedValue[];
}

// Every tile and chart on the Dashboard from ONE request, asking for only the
// fields they need — not the full-fat list queries, which pull every column of
// every row just to read `.length`. All figures are derived client-side below.
const DASHBOARD_QUERY = `
  query DashboardStats($organizationId: String) {
    products(organizationId: $organizationId) {
      id
      totalStock
      reorderLevel
      sellingPrice
      currency
      categoryId
    }
    orders(organizationId: $organizationId) {
      id
      orderDate
      total
      status
    }
    sales(organizationId: $organizationId) {
      id
      saleDate
      total
      items { productId productName lineTotal }
    }
    categories(organizationId: $organizationId) {
      id
      name
    }
  }
`;

interface DashboardResponse {
  products: {
    id: string;
    totalStock: number;
    reorderLevel?: number | null;
    sellingPrice?: number | null;
    currency?: string | null;
    categoryId?: string | null;
  }[];
  orders: { id: string; orderDate?: string | null; total?: number | null; status?: string | null }[];
  sales: {
    id: string;
    saleDate?: string | null;
    total?: number | null;
    items?: { productId?: string | null; productName?: string | null; lineTotal?: number | null }[] | null;
  }[];
  categories: { id: string; name: string }[];
}

const MONTHS_SHOWN = 6;
/** How many rows a ranked chart shows before the tail is dropped. */
const RANK_LIMIT = 5;

const num = (value?: number | null): number =>
  typeof value === 'number' && isFinite(value) ? value : 0;

// Built from the calendar, not from the rows returned, so a month with no
// trade shows as a gap instead of being closed up.
const emptyMonths = (now: Date): MonthPoint[] => {
  const points: MonthPoint[] = [];
  for (let back = MONTHS_SHOWN - 1; back >= 0; back -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - back, 1);
    points.push({
      key: `${date.getFullYear()}-${date.getMonth()}`,
      // Device locale, matching money() — a Bengali handset gets Bengali month
      // names without this file hardcoding a market.
      label: date.toLocaleDateString(undefined, { month: 'short' }),
      sales: 0,
      purchases: 0,
    });
  }
  return points;
};

const monthKey = (raw?: string | null): string | null => {
  if (!raw) return null;
  const date = new Date(raw);
  if (isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth()}`;
};

/** The biggest `RANK_LIMIT` entries, largest first, zeroes dropped. */
const topBy = (totals: Map<string, { label: string; value: number }>): NamedValue[] =>
  [...totals.entries()]
    .map(([key, entry]) => ({ key, label: entry.label, value: entry.value }))
    .filter((entry) => entry.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, RANK_LIMIT);

export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDashboardStats: builder.query<DashboardStats, string | void>({
      query: (organizationId) => ({
        body: {
          query: DASHBOARD_QUERY,
          variables: { organizationId: organizationId ?? null },
        },
      }),
      transformResponse: (res: unknown): DashboardStats => {
        const data = (res ?? {}) as Partial<DashboardResponse>;
        const products = data.products ?? [];
        const orders = data.orders ?? [];
        const sales = data.sales ?? [];
        const categories = data.categories ?? [];

        const months = emptyMonths(new Date());
        const byMonth = new Map(months.map((month) => [month.key, month]));
        sales.forEach((sale) => {
          const bucket = byMonth.get(monthKey(sale.saleDate) ?? '');
          if (bucket) bucket.sales += num(sale.total);
        });
        orders.forEach((order) => {
          const bucket = byMonth.get(monthKey(order.orderDate) ?? '');
          if (bucket) bucket.purchases += num(order.total);
        });

        // Statuses are counted as they arrive rather than against a hardcoded
        // list, so one added server-side shows up here instead of vanishing
        // out of a total that still claims to be complete.
        const statusTotals = new Map<string, { label: string; value: number }>();
        orders.forEach((order) => {
          const key = (order.status ?? 'unknown').toLowerCase();
          const entry = statusTotals.get(key) ?? { label: key, value: 0 };
          entry.value += 1;
          statusTotals.set(key, entry);
        });

        // Keyed by productId so a renamed product still accumulates onto one
        // row; the name is only what that row gets labelled with.
        const productTotals = new Map<string, { label: string; value: number }>();
        sales.forEach((sale) => {
          (sale.items ?? []).forEach((item) => {
            const key = item.productId ?? item.productName ?? '';
            if (!key) return;
            const entry = productTotals.get(key) ?? { label: item.productName ?? key, value: 0 };
            entry.value += num(item.lineTotal);
            productTotals.set(key, entry);
          });
        });

        // Exclusive buckets, evaluated out → low → healthy so each product
        // lands in exactly one.
        const stockHealth = { healthy: 0, low: 0, out: 0 };
        products.forEach((product) => {
          const stock = num(product.totalStock);
          const reorder = num(product.reorderLevel);
          if (stock <= 0) stockHealth.out += 1;
          else if (reorder > 0 && stock <= reorder) stockHealth.low += 1;
          else stockHealth.healthy += 1;
        });

        const categoryNames = new Map(categories.map((category) => [category.id, category.name]));
        const categoryTotals = new Map<string, { label: string; value: number }>();
        const categoryCounts = new Map<string, { label: string; value: number }>();
        let inventoryValue = 0;
        products.forEach((product) => {
          const value = num(product.sellingPrice) * num(product.totalStock);
          inventoryValue += value;
          const key = product.categoryId ?? '';
          // An uncategorised product still counts toward the valuation above;
          // it just has no bar to sit in.
          if (!key) return;
          const entry = categoryTotals.get(key) ?? { label: categoryNames.get(key) ?? key, value: 0 };
          entry.value += value;
          categoryTotals.set(key, entry);

          const counted = categoryCounts.get(key) ?? { label: entry.label, value: 0 };
          counted.value += 1;
          categoryCounts.set(key, counted);
        });

        return {
          productCount: products.length,
          // Same rule the screen used before: only products with a reorder
          // level set can be "low", so a level of 0/undefined never counts.
          lowStockCount: products.filter(
            (p) => (p.reorderLevel ?? 0) > 0 && p.totalStock <= (p.reorderLevel ?? 0),
          ).length,
          outOfStockCount: products.filter((p) => num(p.totalStock) <= 0).length,
          stockHealth,
          orderCount: orders.length,
          saleCount: sales.length,
          inventoryValue,
          salesValue: sales.reduce((sum, sale) => sum + num(sale.total), 0),
          purchaseValue: orders.reduce((sum, order) => sum + num(order.total), 0),
          currency: products.find((p) => p.currency)?.currency ?? undefined,
          monthly: months,
          orderStatus: [...statusTotals.entries()]
            .map(([key, entry]) => ({ key, label: entry.label, value: entry.value }))
            .sort((a, b) => b.value - a.value),
          topProducts: topBy(productTotals),
          categoryValue: topBy(categoryTotals),
          categoryCount: [...categoryCounts.entries()]
            .map(([key, entry]) => ({ key, label: entry.label, value: entry.value }))
            .sort((a, b) => b.value - a.value),
        };
      },
      // Counts go stale whenever any of the three collections change, so ride
      // the same LIST tags the individual list queries already invalidate.
      providesTags: [
        { type: 'Product', id: 'LIST' },
        { type: 'Order', id: 'LIST' },
        { type: 'Sale', id: 'LIST' },
      ],
    }),
  }),
});

export const { useGetDashboardStatsQuery } = dashboardApi;
